import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { EInterceptorOptionsHTTPHeaders } from "src/app/_helpers/interceptors/EInterceptorOptionsHTTPHeaders";
import { Utilities } from "src/app/_helpers/utility/utilities";
import { IAnalysisSummary, IAnalysis } from "src/app/_interfaces/IAnalysis";
import { ILandscapeJobInformation } from "src/app/_interfaces/ILandscapeJobInformation";
import { WfdssCase } from "src/app/_models/incident/wfdssCase/wfdssCase";
// import { environment } from "../../../environments/environment";
import { ConfigService } from "../config.service";
import { IFsProWeather } from "src/app/_interfaces/IFmsf";
import { UploadLayer } from "src/app/_models/map/map-layers/uploadLayer";
import { IShapefileUpload } from "src/app/_interfaces/IShapefileUpload";
import { IFsProJobInformation } from "src/app/_interfaces/IFSProJobInformation";
import { EditRuleType } from "src/app/_interfaces/ILandscapeEdits";
import { ILandscapeParameters } from "src/app/_interfaces/ILandscapeParameters";
import { IWFDSSFSProWx } from "src/app/_components/case-dashboard/panels/analysis-panel/edit-analysis/fs-pro/fs-pro-weather/fs-pro-weather-types-interfaces";
@Injectable({
    providedIn: "root",
})
/**
 * @description Only data calls in this layer.
 * @deprecated This was a part of the experimental 'facade' design pattern. Was following the writeup from https://dev-academy.com/angular-architecture-best-practices */
export class AnalysisDataService {
    private readonly baseUrl = this.environment.analysisServiceApi.baseUrl;
    constructor(private httpSvc: HttpClient, private environment: ConfigService) {}
    putAnalysisParameters(analysis: IAnalysis) {
        return this.httpSvc.put<IAnalysis>(this.baseUrl + "/analysis", analysis);
    }
    saveAnalysisSummary(analysisSummary: IAnalysisSummary) {
        return this.httpSvc.put<IAnalysisSummary>(this.baseUrl + "/case/analysisSummary", analysisSummary);
    }
    /**
     * @param analysis with no Id,
     * @returns
     * @description Omit analysisId and provide title, start date, request note and a status of 'requested' to create a new Analysis object.
     */
    putSaveAnalysisRequestParameters(analysis: IAnalysis) {
        return this.httpSvc.put<IAnalysis>(this.baseUrl + "/analysis/requestAnalysis", analysis);
    }
    getSaveAcceptAnalysis(analysis: IAnalysis) {
        return this.httpSvc.get<IAnalysis>(this.baseUrl + `/analysis/${analysis.analysisId}/acceptAnalysis`);
    }

    getExistingAnalysisForWfdssCaseId(wfdssCase: WfdssCase) {
        return this.httpSvc.get<IAnalysis[]>(`${this.baseUrl + "/analysis/wfdssCase/" + wfdssCase.wfdssCaseId}`, { params: { limit: "200" } });
    }

    getAnalysisSummary(wfdssCase: WfdssCase) {
        var supressInterceptors = [EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor, EInterceptorOptionsHTTPHeaders.SuppressLoadingInterceptor];
        return this.httpSvc.get<IAnalysisSummary>(`${this.baseUrl + "/case/" + wfdssCase.wfdssCaseId + '/analysisSummary'}`, {
            headers: Utilities.appendHTTPHeaderForInterceptor(supressInterceptors),
        });
    }

    saveReadyForPublication(wfdssCaseId: number, isRFP: boolean) {
        // var supressInterceptors = [EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor, EInterceptorOptionsHTTPHeaders.SuppressLoadingInterceptor];
        return this.httpSvc.get<IAnalysisSummary>(`${this.baseUrl + "/case/" + wfdssCaseId + '/analysisSummary/isReadyForPublication'}`, 
            {
                params: { isReadyForPublication :  isRFP},
                //headers: Utilities.appendHTTPHeaderForInterceptor(supressInterceptors)
            }
        );
    }

    /**
     * @description Saves the current landscape parameters.
     * @warning This is NOT the same as creating a landscape file.*/
    saveLandscapeParameters(landscapeParams: ILandscapeParameters, analysis: IAnalysis) {
        return this.httpSvc.put<any>(`${this.baseUrl + "/analysis/" + analysis.analysisId + "/landscapeParameters"}`, landscapeParams);
    }

    getLandscapeParameters(analysis: IAnalysis): Observable<ILandscapeParameters> {
        return this.httpSvc.get<ILandscapeParameters>(`${this.baseUrl + "/analysis/" + analysis.analysisId + "/landscapeParameters"}`);
    }

    getAnalysis(analysisId: number): Observable<IAnalysis> {
        return this.httpSvc.get<IAnalysis>(this.baseUrl + "/analysis/" + analysisId);
    }

    getLandfireProductValues(): Observable<[{ productName: string; fuelModelSelector: string }]> {
        return this.httpSvc.get<[{ productName: string; fuelModelSelector: string }]>(this.baseUrl + "/analysis/" + "landfireProductValues");
    }
    /**
     * @description Submits the currently saved landscape to the landfire job service.
     * @warning only one landscape file can exist at a time. This will replace any other landscape file in existence for a given analysis * */
    submitLandscapeJobRequest(analysis: IAnalysis, landscapeParams: ILandscapeParameters) {
        return this.httpSvc.post<any>(`${this.baseUrl + "/analysis/" + analysis.analysisId + "/submitLandfireJob"}`, landscapeParams);
    }

    /**
     * @description Retrieves information and status about the landscape which has been submitted to LFPS.*/
    getLandfireJobInformation(analysis: IAnalysis) {
        var supressInterceptors = [EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor, EInterceptorOptionsHTTPHeaders.SuppressLoadingInterceptor];
        return this.httpSvc.get<ILandscapeJobInformation>(`${this.baseUrl + "/analysis/" + analysis.analysisId + "/landfireJob"}`, {
            headers: Utilities.appendHTTPHeaderForInterceptor(supressInterceptors),
        });
    }

    v2saveFsProWeather(analysis: IAnalysis, weather: IWFDSSFSProWx): Observable<IWFDSSFSProWx> {
        return this.httpSvc.put<IWFDSSFSProWx>(`${this.baseUrl}/analysis/${analysis.analysisId}/fspro/weather`, weather);
    }
    v2getFsProWeather(analysis: IAnalysis) {
        const supressInterceptors = [EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor];
        return this.httpSvc.get<IWFDSSFSProWx>(`${this.baseUrl}/analysis/${analysis.analysisId}/fspro/weather`, {
            headers: Utilities.appendHTTPHeaderForInterceptor(supressInterceptors),
        });
    }

    saveFsProWeather(analysis: IAnalysis, weather: IFsProWeather): Observable<any> {
        return this.httpSvc.put<IFsProWeather>(`${this.baseUrl}/analysis/${analysis.analysisId}/fspro/weather`, weather);
    }

    saveFsproAnalysisCopy(newTitle: string, copyID: number){
        return this.httpSvc.put<any>( `${this.baseUrl}/analysis/${copyID}/actions/copy`, newTitle)
    }
    getFsProWeather(analysis: IAnalysis) {
        const supressInterceptors = [EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor];
        return this.httpSvc.get<IFsProWeather>(`${this.baseUrl}/analysis/${analysis.analysisId}/fspro/weather`, {
            headers: Utilities.appendHTTPHeaderForInterceptor(supressInterceptors),
        });
    }

    //Get Run Status https://wfdssdev.firenet.gov/analysisservice/analysis/1/fspro/job/run
    getFsProJobInformation(analysis: IAnalysis): Observable<IFsProJobInformation> {
        const supressInterceptors = [EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor, EInterceptorOptionsHTTPHeaders.SuppressLoadingInterceptor];
        return this.httpSvc.get<any>(`${this.baseUrl}/analysis/${analysis.analysisId}/fspro/job/run`, {
            headers: Utilities.appendHTTPHeaderForInterceptor(supressInterceptors),
        });
    }
    //Submit Run: https://wfdssdev.firenet.gov/analysisservice/analysis/1/fspro/job/run?action=submit
    runFsProJob(analysis: IAnalysis): Observable<any> {
        return this.httpSvc.get<any>(`${this.baseUrl}/analysis/${analysis.analysisId}/fspro/job/run`, {
            params: { action: "submit" },
        });
    }
    //Terminate Run: https://wfdssdev.firenet.gov/analysisservice/analysis/1/fspro/job/run?action=terminate
    terminateFsProJob(analysis: IAnalysis): Observable<any> {
        return this.httpSvc.get<any>(`${this.baseUrl}/analysis/${analysis.analysisId}/fspro/job/run`, {
            params: { action: "terminate" },
        });
    }
    /**
     * @description Submits the zip file and layer information to the shapefileUpload PUT service
     * @param uploadLayer - the parameters for the new layer to import
     * @param file - the zip file data to upload and import
     * @returns Observable with request response
     */
    uploadShapefile(uploadLayer, file): Observable<any> {
        return this.httpSvc.put<any>(`${this.baseUrl}/shapefileUpload`, file, {
            params: uploadLayer,
        });
    }
    /**
     * @description Gets information about the shapefileUpload status
     * @param shapefileUpload - the shapefileUpload job status to check
     * @returns Observable with the request response
     */
    getUploadShapefileJobInformation(jobId: number): Observable<any> {
        return this.httpSvc.get<any>(`${this.baseUrl}/shapefileUploadJob/${jobId}`);
    }

   
    getFsProJobOutputTxt(analysis: IAnalysis, type: EFsProModelOutputType) : Observable<string> {
        const supressInterceptors = [EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor, EInterceptorOptionsHTTPHeaders.SuppressLoadingInterceptor]; // output zip size is so large that interceptors break
        let requestHeaders = Utilities.appendHTTPHeaderForInterceptor(supressInterceptors);        
        const url = `${this.baseUrl}/analysis/${analysis.analysisId}/fspro/job/output`;
        return this.httpSvc.get(url, { responseType: "text", observe:"body", params: { outputType: type }, headers: requestHeaders  });
    }
    getFsProJobOutputZip(analysis: IAnalysis, type: EFsProModelOutputType) {
        const supressInterceptors = [EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor, EInterceptorOptionsHTTPHeaders.SuppressLoadingInterceptor]; // output zip size is so large that interceptors break
        let requestHeaders = Utilities.appendHTTPHeaderForInterceptor(supressInterceptors);
        requestHeaders = requestHeaders.append("Accept", "application/zip");
        const url = `${this.baseUrl}/analysis/${analysis.analysisId}/fspro/job/output`;
        return this.httpSvc.get(url, { responseType: "arraybuffer", params: { outputType: type } });
    }
    getLandscapeZip(analysisId: number) {
        const supressInterceptors = [EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor, EInterceptorOptionsHTTPHeaders.SuppressLoadingInterceptor]; // output zip size is so large that interceptors break
        let requestHeaders = Utilities.appendHTTPHeaderForInterceptor(supressInterceptors);
        requestHeaders = requestHeaders.append("Accept", "application/zip");
        const url = `${this.baseUrl}/analysis/${analysisId}/landfireJob/outputFile`;
        return this.httpSvc.get(url, { responseType: "arraybuffer"});
    }
    putSaveLandscapeEditRules(analysis: IAnalysis, rules: any){        
        return this.httpSvc.put<EditRuleType[]>(`${this.baseUrl}/analysis/${analysis.analysisId}/editRules`, rules)
    }
    getLandscapeEditRules(analysis: IAnalysis){        
        return this.httpSvc.get<EditRuleType[]>(`${this.baseUrl}/analysis/${analysis.analysisId}/editRules`)
    }
    getFailedFsProInputFile(analysis: IAnalysis){
        return this.httpSvc.get(`${this.baseUrl}/analysis/${analysis.analysisId}/fspro/generateFsproInputFile`, {responseType: "text", observe: "response"})
    }

}


export enum EFsProModelOutputType  {
    COMPLETE_ZIP = "output-zip",
    DAILY_ACRES_TXT= "daily-acres-txt",
    EVENT_COVERAGE_TXT="event-coverage-txt"
}